/** @format */

import { useCallback, useMemo, useRef, useState } from 'react';
import { Check, Pipette } from 'lucide-react';
import { cn, Input, Popover, PopoverContent, PopoverTrigger } from '@clarix/ui';

// ── Color palette ─────────────────────────────────────────────────────

const PRESET_COLORS = [
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#22C55E',
  '#06B6D4',
  '#3B82F6',
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#14B8A6',
  '#84CC16',
  '#A855F7',
  '#DC2626',
  '#0EA5E9',
  '#6B7280',
  '#1E293B',
];

const DEFAULT_COLOR = '#6366F1';

// ── Helpers ───────────────────────────────────────────────────────────

function hexToHSL(hex: string): { h: number; s: number; l: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return { h, s, l };
}

function hexToHSB(hex: string): { h: number; s: number; b: number } {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const bl = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, bl);
  const min = Math.min(r, g, bl);
  const b = max;
  const s = max === 0 ? 0 : (max - min) / max;
  let h = 0;
  if (max !== min) {
    const d = max - min;
    if (max === r) h = ((g - bl) / d + (g < bl ? 6 : 0)) / 6;
    else if (max === g) h = ((bl - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h, s, b };
}

function hsbToHex(h: number, s: number, b: number): string {
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = b * (1 - s);
  const q = b * (1 - f * s);
  const t = b * (1 - (1 - f) * s);
  let r = 0,
    g = 0,
    bl = 0;
  switch (i % 6) {
    case 0:
      r = b;
      g = t;
      bl = p;
      break;
    case 1:
      r = q;
      g = b;
      bl = p;
      break;
    case 2:
      r = p;
      g = b;
      bl = t;
      break;
    case 3:
      r = p;
      g = q;
      bl = b;
      break;
    case 4:
      r = t;
      g = p;
      bl = b;
      break;
    case 5:
      r = b;
      g = p;
      bl = q;
      break;
  }
  const toHex = (v: number) =>
    Math.round(v * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(bl)}`.toUpperCase();
}

function isLightColor(hex: string): boolean {
  const { l } = hexToHSL(hex);
  return l > 0.65;
}

// ── Saturation / Brightness Panel ─────────────────────────────────────

interface SatBrightPanelProps {
  hue: number;
  saturation: number;
  brightness: number;
  /** Live local updates while dragging (preview only). */
  onChange: (s: number, b: number) => void;
  /** Fired once on pointer-up so parents can persist without rate-limiting. */
  onCommit: (s: number, b: number) => void;
}

function SatBrightPanel({ hue, saturation, brightness, onChange, onCommit }: SatBrightPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const lastPointer = useRef<{ s: number; b: number } | null>(null);

  const handlePointer = useCallback(
    (clientX: number, clientY: number) => {
      const rect = panelRef.current?.getBoundingClientRect();
      if (!rect) return;
      const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const b = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height));
      lastPointer.current = { s, b };
      onChange(s, b);
    },
    [onChange],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;
      handlePointer(e.clientX, e.clientY);

      const handleMove = (ev: MouseEvent) => {
        if (dragging.current) handlePointer(ev.clientX, ev.clientY);
      };
      const handleUp = () => {
        dragging.current = false;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
        if (lastPointer.current) onCommit(lastPointer.current.s, lastPointer.current.b);
      };
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [handlePointer, onCommit],
  );

  const pureHueHex = hsbToHex(hue, 1, 1);

  return (
    <div
      ref={panelRef}
      className="relative h-36 w-full cursor-crosshair select-none overflow-hidden rounded-lg"
      style={{ backgroundColor: pureHueHex }}
      onMouseDown={handleMouseDown}
    >
      <div
        className="absolute inset-0 rounded-lg"
        style={{ background: 'linear-gradient(to right, #FFFFFF, transparent)' }}
      />
      <div
        className="absolute inset-0 rounded-lg"
        style={{ background: 'linear-gradient(to bottom, transparent, #000000)' }}
      />
      <div
        className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
        style={{
          left: `${saturation * 100}%`,
          top: `${(1 - brightness) * 100}%`,
          boxShadow: '0 0 0 1px rgba(0,0,0,0.2), 0 2px 4px rgba(0,0,0,0.2)',
        }}
      />
    </div>
  );
}

// ── Hue Slider ────────────────────────────────────────────────────────

interface HueSliderProps {
  hue: number;
  /** Live local updates while dragging (preview only). */
  onChange: (h: number) => void;
  /** Fired once on pointer-up so parents can persist without rate-limiting. */
  onCommit: (h: number) => void;
}

function HueSlider({ hue, onChange, onCommit }: HueSliderProps) {
  const sliderRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const lastHue = useRef<number | null>(null);

  const handlePointer = useCallback(
    (clientX: number) => {
      const rect = sliderRef.current?.getBoundingClientRect();
      if (!rect) return;
      const h = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      lastHue.current = h;
      onChange(h);
    },
    [onChange],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      dragging.current = true;
      handlePointer(e.clientX);

      const handleMove = (ev: MouseEvent) => {
        if (dragging.current) handlePointer(ev.clientX);
      };
      const handleUp = () => {
        dragging.current = false;
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleUp);
        if (lastHue.current !== null) onCommit(lastHue.current);
      };
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleUp);
    },
    [handlePointer, onCommit],
  );

  return (
    <div
      ref={sliderRef}
      className="relative h-3 w-full cursor-pointer select-none rounded-full"
      style={{
        background:
          'linear-gradient(to right, #FF0000, #FFFF00, #00FF00, #00FFFF, #0000FF, #FF00FF, #FF0000)',
      }}
      onMouseDown={handleMouseDown}
    >
      <div
        className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white"
        style={{
          left: `${hue * 100}%`,
          boxShadow: '0 0 0 1px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.2)',
        }}
      />
    </div>
  );
}

// ── Picker Panel (inner) — keyed on `currentColor` to reset state ─────

interface PickerPanelProps {
  initialColor: string;
  onColorChange: (hex: string) => void;
  showPresets?: boolean;
  /**
   * When true, also notify `onColorChange` on every drag move (live preview).
   * Default false — parents that persist on change (masters inline color) only
   * get one callback per drag gesture (pointer-up), avoiding rate-limit storms.
   */
  liveUpdate?: boolean;
}

export function PickerPanel({
  initialColor,
  onColorChange,
  showPresets = true,
  liveUpdate = false,
}: PickerPanelProps) {
  const initialHsb = useMemo(() => hexToHSB(initialColor), [initialColor]);
  const [hue, setHue] = useState(initialHsb.h);
  const [saturation, setSaturation] = useState(initialHsb.s);
  const [brightness, setBrightness] = useState(initialHsb.b);
  const [hexInput, setHexInput] = useState(initialColor);

  const derivedColor = useMemo(
    () => hsbToHex(hue, saturation, brightness),
    [hue, saturation, brightness],
  );

  const applySB = useCallback(
    (s: number, b: number, commit: boolean) => {
      setSaturation(s);
      setBrightness(b);
      const hex = hsbToHex(hue, s, b);
      setHexInput(hex);
      if (commit || liveUpdate) onColorChange(hex);
    },
    [hue, liveUpdate, onColorChange],
  );

  const applyHue = useCallback(
    (h: number, commit: boolean) => {
      setHue(h);
      const hex = hsbToHex(h, saturation, brightness);
      setHexInput(hex);
      if (commit || liveUpdate) onColorChange(hex);
    },
    [saturation, brightness, liveUpdate, onColorChange],
  );

  const handleSBChange = useCallback((s: number, b: number) => applySB(s, b, false), [applySB]);
  const handleSBCommit = useCallback((s: number, b: number) => applySB(s, b, true), [applySB]);
  const handleHueChange = useCallback((h: number) => applyHue(h, false), [applyHue]);
  const handleHueCommit = useCallback((h: number) => applyHue(h, true), [applyHue]);

  const handlePresetClick = useCallback(
    (color: string) => {
      const newHsb = hexToHSB(color);
      setHue(newHsb.h);
      setSaturation(newHsb.s);
      setBrightness(newHsb.b);
      setHexInput(color);
      onColorChange(color);
    },
    [onColorChange],
  );

  const handleHexInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setHexInput(raw);
      if (/^#[0-9A-Fa-f]{6}$/.test(raw)) {
        const upper = raw.toUpperCase();
        const newHsb = hexToHSB(upper);
        setHue(newHsb.h);
        setSaturation(newHsb.s);
        setBrightness(newHsb.b);
        onColorChange(upper);
      }
    },
    [onColorChange],
  );

  return (
    <div className="space-y-3 px-4 pb-4">
      <SatBrightPanel
        hue={hue}
        saturation={saturation}
        brightness={brightness}
        onChange={handleSBChange}
        onCommit={handleSBCommit}
      />

      <HueSlider hue={hue} onChange={handleHueChange} onCommit={handleHueCommit} />

      <div className="flex items-center gap-2">
        <div
          className="h-8 w-8 shrink-0 rounded-md border border-gray-200 shadow-sm"
          style={{ backgroundColor: derivedColor }}
        />
        <Input
          value={hexInput}
          onChange={handleHexInputChange}
          placeholder="#6366F1"
          className="h-8 flex-1 text-lg"
        />
      </div>

      {showPresets && (
        <div>
          <p className="text-muted-foreground mb-1.5 text-xs font-medium uppercase tracking-wider">
            Presets
          </p>
          <div className="grid grid-cols-8 gap-1.5">
            {PRESET_COLORS.map((color) => {
              const isSelected = derivedColor.toUpperCase() === color.toUpperCase();
              return (
                <button
                  key={color}
                  type="button"
                  onClick={() => handlePresetClick(color)}
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-md transition-all',
                    isSelected
                      ? 'scale-110 ring-2 ring-indigo-500 ring-offset-1'
                      : 'hover:scale-110 hover:shadow-md',
                  )}
                  style={{ backgroundColor: color }}
                  title={color}
                >
                  {isSelected && (
                    <Check
                      className={cn(
                        'h-3 w-3 drop-shadow-sm',
                        isLightColor(color) ? 'text-gray-700' : 'text-white',
                      )}
                      strokeWidth={2.5}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ColorPicker ──────────────────────────────────────────────────

interface ColorPickerProps {
  value: string | null;
  onChange: (color: string) => void;
  defaultColor?: string;
  /** Render as a compact dot trigger (for inline table use) */
  compact?: boolean;
}

export function ColorPicker({
  value,
  onChange,
  defaultColor = DEFAULT_COLOR,
  compact = false,
}: ColorPickerProps) {
  const currentColor = value ?? defaultColor;
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {compact ? (
          <button
            type="button"
            className="focus-visible:ring-ring h-5 w-5 cursor-pointer rounded border border-gray-200 transition-all hover:scale-110 hover:shadow-md focus-visible:outline-none focus-visible:ring-2"
            style={{ backgroundColor: currentColor }}
            title={currentColor}
          />
        ) : (
          <button
            type="button"
            className={cn(
              'border-input bg-background flex h-9 w-full items-center gap-2.5 rounded-md border px-3',
              'hover:bg-accent/50 focus-visible:ring-ring text-xl transition-colors focus-visible:outline-none focus-visible:ring-2',
            )}
          >
            <div
              className="h-5 w-5 shrink-0 rounded-md border border-gray-200 shadow-sm"
              style={{ backgroundColor: currentColor }}
            />
            <span className="text-muted-foreground text-lg">{currentColor}</span>
            <Pipette className="text-muted-foreground ml-auto h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        )}
      </PopoverTrigger>

      <PopoverContent className="w-[272px] p-3" align="start" sideOffset={4}>
        <PickerPanel
          key={open ? 'open' : 'closed'}
          initialColor={currentColor}
          onColorChange={onChange}
        />
      </PopoverContent>
    </Popover>
  );
}
